<template>
  <div class="upload-card">
    <div class="upload-card-header">
      <h5 class="mb-1">Planilha {{ position }}</h5>
      <vs-button
        size="small"
        color="danger"
        type="border"
        icon-pack="feather"
        icon="icon-trash-2"
        title="Remover esta planilha"
        @click="$emit('remove')"
      />
    </div>

    <div class="upload-card-body">
      <div v-if="!card.publicUrl" class="upload-input-wrap">
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls,.csv"
          class="upload-file-hidden"
          :disabled="card.uploading || disableUpload"
          @change="onFileChosen"
        />
        <vs-button
          color="primary"
          type="border"
          icon-pack="feather"
          icon="icon-upload"
          :disabled="card.uploading || disableUpload"
          @click="triggerFilePicker"
        >
          {{ card.uploading ? 'Enviando arquivo...' : 'Selecionar arquivo (.xlsx, .xls ou .csv)' }}
        </vs-button>
        <p v-if="disableUpload" class="upload-status warning mt-2">
          Marque ao menos uma dash acima antes de subir uma planilha.
        </p>
        <p v-if="card.uploadError" class="upload-status error mt-2">{{ card.uploadError }}</p>
      </div>

      <div v-else class="card-grid">
        <div class="card-col-left">
          <p class="upload-status ok mb-2">
            <feather-icon icon="CheckCircleIcon" svg-classes="h-4 w-4" />
            <strong class="ml-1">{{ card.fileName }}</strong>
          </p>

          <div v-if="card.sheets.length > 1">
            <label class="input-label">Aba da planilha</label>
            <vs-select :value="card.sheetIndex" class="w-full" @input="onSheetChanged">
              <vs-select-item
                v-for="sheet in card.sheets"
                :key="sheet.index"
                :value="sheet.index"
                :text="sheet.name"
              />
            </vs-select>
          </div>

          <div v-if="currentSheetColumns.length" class="mt-3">
            <div class="d-flex" style="justify-content: space-between; align-items: baseline;">
              <label class="input-label mb-0">Pré-visualização</label>
              <span class="preview-meta">
                {{ currentSheetTotalRows }} {{ currentSheetTotalRows === 1 ? 'contato' : 'contatos' }} no total · mostrando os {{ currentSheetPreview.length }} primeiros
              </span>
            </div>
            <div class="preview-table-wrap mt-1">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th v-for="col in currentSheetColumns" :key="col">{{ col }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIdx) in currentSheetPreview" :key="rowIdx">
                    <td v-for="(cell, cellIdx) in row" :key="cellIdx">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="mt-3">
            <label class="input-label">Coluna do nome</label>
            <vs-select :value="card.nameColumn" class="w-full" @input="updateField('nameColumn', $event)">
              <vs-select-item value="" text="Selecione a coluna" />
              <vs-select-item
                v-for="col in currentSheetColumns"
                :key="`name-${col}`"
                :value="col"
                :text="col"
              />
            </vs-select>
          </div>

          <div class="mt-2">
            <label class="input-label">Coluna do telefone</label>
            <vs-select :value="card.phoneColumn" class="w-full" @input="updateField('phoneColumn', $event)">
              <vs-select-item value="" text="Selecione a coluna" />
              <vs-select-item
                v-for="col in currentSheetColumns"
                :key="`phone-${col}`"
                :value="col"
                :text="col"
              />
            </vs-select>
          </div>

          <div class="mt-2">
            <label class="input-label">Coluna do e-mail (opcional)</label>
            <vs-select :value="card.emailColumn" class="w-full" @input="updateField('emailColumn', $event)">
              <vs-select-item value="" text="Não usar" />
              <vs-select-item
                v-for="col in currentSheetColumns"
                :key="`email-${col}`"
                :value="col"
                :text="col"
              />
            </vs-select>
          </div>
        </div>

        <div class="card-col-right">
          <label class="input-label">Nome da categoria</label>
          <vs-input
            :value="card.groupName"
            class="w-full"
            placeholder="Ex: Clientes fiéis"
            @input="updateField('groupName', $event)"
          />

          <label class="input-label mt-3">Cor da categoria</label>
          <color-tag-picker :value="card.colorTag" @input="updateField('colorTag', $event)" />

          <label class="input-label mt-3">Dashes que recebem esta planilha</label>
          <div class="d-flex" style="gap: 6px; align-items: center; margin-bottom: 6px;">
            <vs-button size="small" type="flat" color="primary" @click="$emit('set-all-tenants', true)">
              Marcar todas
            </vs-button>
            <vs-button size="small" type="flat" color="warning" @click="$emit('set-all-tenants', false)">
              Limpar
            </vs-button>
          </div>
          <div class="tenant-checks">
            <label
              v-for="tenant in availableTenants"
              :key="`${card.localId}-${tenant.id}`"
              class="tenant-check"
              :class="{ active: card.tenantIds.includes(tenant.id) }"
            >
              <input
                type="checkbox"
                :value="tenant.id"
                :checked="card.tenantIds.includes(tenant.id)"
                @change="$emit('toggle-tenant', tenant.id)"
              />
              <span>{{ tenant.label }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ColorTagPicker from '@/components/group-of-customer/ColorTagPicker.vue';

export default {
  name: 'MassCustomerCategoryUploadCard',
  components: {
    ColorTagPicker,
  },
  props: {
    card: {
      type: Object,
      required: true,
    },
    position: {
      type: Number,
      required: true,
    },
    availableTenants: {
      type: Array,
      required: true,
    },
    disableUpload: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    currentSheetColumns() {
      const sheet = (this.card.sheets || []).find((item) => item.index === this.card.sheetIndex);
      return sheet && Array.isArray(sheet.columns) ? sheet.columns : [];
    },
    currentSheetPreview() {
      const sheet = (this.card.sheets || []).find((item) => item.index === this.card.sheetIndex);
      return sheet && Array.isArray(sheet.preview_rows) ? sheet.preview_rows : [];
    },
    currentSheetTotalRows() {
      const sheet = (this.card.sheets || []).find((item) => item.index === this.card.sheetIndex);
      if (sheet && typeof sheet.total_rows === 'number') return sheet.total_rows;
      return sheet && Array.isArray(sheet.preview_rows) ? sheet.preview_rows.length : 0;
    },
  },
  methods: {
    triggerFilePicker() {
      const input = this.$refs.fileInput;
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    onFileChosen(event) {
      const file = event && event.target && event.target.files && event.target.files[0];
      if (!file) return;
      this.$emit('file-chosen', file);
      if (event.target) {
        event.target.value = '';
      }
    },
    onSheetChanged(sheetIndex) {
      this.$emit('sheet-changed', sheetIndex);
    },
    updateField(field, value) {
      this.$emit('update-field', { field, value });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles.scss';
</style>
